import {
  getUppdragDateAsString,
  shouldShowWaitingForTimeReportConfirmationPing,
  Uppdrag,
  useIsCurrentUserAuthorizedForModifyingUppdrag,
} from "types/uppdrag.type";
import { usePatchUppdragLogic } from "./useCreateOrUpdateUppdragLogic";
import { PatchUppdragModal } from "./UppdragModal";
import { PriorityIcon } from "pages/app/uppdrag-list-page/PriorityIcon";
import { ContentContainer, ShadowCard } from "components/ShadowCard";

import { UppdragStatusButton } from "./UppdragStatusButton";
import DangerModal from "components/DangerModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useNotification } from "hooks/useNotification";
import { deleteUppdrag, patchUppdrag, UppdragQueryKey } from "api/uppdrag.api";
import { useCurrentCompany } from "api/company.api";
import PrimaryButton from "components/buttons/PrimaryButton";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { DotMenu } from "components/DotMenu";
import { ProxyAssignmentIcon } from "components/ProxyAssignmentIcon";
import { Link } from "components/Link";
import { Dot } from "components/Dot";
import { UppgiftTab } from "../uppgifter/UppgiftTab";
import { Headline4 } from "components/typography/Headline4";
import {
  Item,
  Label,
  MultiColumnList,
  Value,
} from "components/multiColumnList/MultiColumnList";
import Divider from "components/Divider";
import { useIsAdmin } from "store/user.store";
import { WasteStationNotificationStatusBadge } from "components/WasteStationNotificationStatusBadge";
import {
  FilePreviewSmall,
  InspectionPDFPreviewSmall,
} from "components/FilePreviewSmall";
import { usePreviewPdf, getPdfProxyUrl } from "hooks/usePreviewPdf";
import { isPdf } from "helpers/file";
import { formatDate } from "helpers/date";
import { useIsFeatureFlagEnabled } from "hooks/useFeatureFlag";
import { FeatureFlagName } from "api/featureFlag.api";
import { useCurrentUser } from "api/user.api";
import { Role } from "types/enums";
import { UppdragOverviewOperator } from "./UppdragOverviewOperator";

type Props = {
  uppdrag: Uppdrag;
};
export const UppdragOverview = ({ uppdrag }: Props) => {
  const { user } = useCurrentUser();

  if (user && user.role <= Role.Operator) {
    return <UppdragOverviewOperator uppdrag={uppdrag} />;
  }

  return <UppdragOverviewAdmin uppdrag={uppdrag} />;
};

const UppdragOverviewAdmin = ({ uppdrag }: Props) => {
  const logic = usePatchUppdragLogic({ uppdragToEdit: uppdrag });

  const { data: currentCompany } = useCurrentCompany();

  const canModify = useIsCurrentUserAuthorizedForModifyingUppdrag(uppdrag);

  const { previewPdf, isLoading, currentUrl } = usePreviewPdf();

  const isWasteStationNotificationEnabled = useIsFeatureFlagEnabled(
    FeatureFlagName.IsWasteStationNotificationEnabled,
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const n = useNotification();

  const deleteMutation = useMutation({
    ...deleteUppdrag(uppdrag.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
      navigate({ to: "/app/uppdrag" });
    },
    onError: () => {
      n.error();
      setIsDeleteModalOpen(false);
    },
  });

  return (
    <ShadowCard className="max-w-[1800px]">
      <ContentContainer>
        <div className="mb-4 flex items-center justify-between">
          <Headline4 className="text-lg sm:text-base">Detaljer</Headline4>
          {canModify && (
            <DotMenu
              items={[
                {
                  label: "Redigera",
                  onClick: logic.openModal,
                },
                {
                  label: "Ta bort uppdrag",
                  onClick: () => setIsDeleteModalOpen(true),
                  isDelete: true,
                },
              ]}
            />
          )}

          <PatchUppdragModal logic={logic} />
          <DangerModal
            isOpen={isDeleteModalOpen}
            title={`Ta bort uppdrag`}
            actionText="Ta bort uppdrag"
            actionHandler={() => deleteMutation.mutate()}
            onClose={() => setIsDeleteModalOpen(false)}
            paragraphs={[`Är du säker på att du vill ta bort uppdraget?`]}
          />
        </div>
        <MultiColumnList>
          {/* <UppdragStatusButton uppdrag={uppdrag} /> */}

          {/* <Item>
            <Label>
              {!!uppdrag.parentCustomer ? `Verksamhetsutövare` : `Kund`}
            </Label>
            <Value>
              <Link
                to={getCustomerPageRoute(uppdrag.facility.customer.id)}
                variant="gray"
              >
                {uppdrag.facility.customer.name}
              </Link>
            </Value>
          </Item> */}
          <Item>
            <Label>Anläggning</Label>
            <Value>
              <Link
                to="/app/kunder/$customerId/facility/$facilityId"
                params={{
                  customerId: uppdrag.facility.customer.id.toString(),
                  facilityId: uppdrag.facility.id.toString(),
                }}
                variant="gray"
              >
                {uppdrag.facility.name}
              </Link>
            </Value>
          </Item>

          <Item>
            <Label>Schemalagt datum</Label>
            <Value>
              <span className="flex items-center">
                {getUppdragDateAsString(uppdrag)}
                {uppdrag.isPrioritized && (
                  <span className="ml-1 flex items-center space-x-2">
                    {`- Prioriterat `}
                    <PriorityIcon />
                  </span>
                )}
              </span>
            </Value>
          </Item>
          <Item>
            <Label>Status</Label>
            <Value>
              <UppdragStatusButton uppdrag={uppdrag} />
            </Value>
          </Item>
          <Item>
            <Label>Operatör</Label>
            <Value>{uppdrag.assignedToUser?.name}</Value>
          </Item>
          <Item>
            <Label>Uppdragsbeskrivning</Label>
            <Value>{uppdrag.description}</Value>
          </Item>
          <Item>
            <Label>Referens</Label>
            <Value>{uppdrag.referenceNumber}</Value>
          </Item>
          {currentCompany?.isInvoicingEnabled && (
            <InvoicingItem uppdrag={uppdrag} />
          )}
          {!!uppdrag.parentCustomer && (
            <Item>
              <Label className="flex items-center">
                Huvudkund
                <div className="ml-1">
                  <ProxyAssignmentIcon />
                </div>
              </Label>
              <Value>
                <Link
                  to="/app/kunder/$customerId"
                  params={{ customerId: uppdrag.parentCustomer.id.toString() }}
                >
                  {uppdrag.parentCustomer.name}
                </Link>
              </Value>
            </Item>
          )}
          {!!uppdrag.externalId && (
            <Item>
              <Label>ID i externt system</Label>
              <Value>{uppdrag.externalId}</Value>
            </Item>
          )}
          {isWasteStationNotificationEnabled &&
            !!uppdrag.wasteStationNotification && (
              <Item>
                <Label>Förfrågan till mottagarstation</Label>
                <Value>
                  <div className="flex flex-col gap-y-3">
                    <div className="flex items-center gap-x-1">
                      <span>
                        {`Lämningsdatum ${formatDate(
                          uppdrag.wasteStationNotification.requestedDate,
                        )}`}
                      </span>
                      <WasteStationNotificationStatusBadge
                        notification={uppdrag.wasteStationNotification}
                      />
                    </div>
                    {!!uppdrag.wasteStationNotification
                      .messageFromWasteStation && (
                      <div className="max-w-sm self-start whitespace-pre-wrap rounded-2xl rounded-tl-sm border border-gray-75 bg-gray-25 px-3.5 py-2.5 text-gray-800">
                        {
                          uppdrag.wasteStationNotification
                            .messageFromWasteStation
                        }
                      </div>
                    )}
                    {uppdrag.wasteStationNotification.fileList.length > 0 && (
                      <div className="flex flex-col gap-y-2">
                        {uppdrag.wasteStationNotification.fileList.map((f) =>
                          isPdf(f.name) ? (
                            <InspectionPDFPreviewSmall
                              key={f.id}
                              inspectionName={f.nameWithoutExtension as string}
                              isLoading={
                                isLoading &&
                                currentUrl === getPdfProxyUrl(f.url)
                              }
                              onClick={() => previewPdf(getPdfProxyUrl(f.url))}
                            />
                          ) : (
                            <FilePreviewSmall
                              key={f.id}
                              url={f.url}
                              name={`${f.name}`}
                            />
                          ),
                        )}
                      </div>
                    )}
                  </div>
                </Value>
              </Item>
            )}
        </MultiColumnList>
        {/* <div></div> */}
        {/* <FacilityDetails uppdrag={uppdrag} /> */}
        <Divider className="my-8" />
        <UppgiftTab uppdrag={uppdrag} />
      </ContentContainer>
    </ShadowCard>
  );
};

type InvoicingProps = {
  uppdrag: Uppdrag;
};
const InvoicingItem = ({ uppdrag }: InvoicingProps) => {
  const isAdmin = useIsAdmin();
  const isWaitingForConfirmation =
    isAdmin && shouldShowWaitingForTimeReportConfirmationPing(uppdrag);

  const qc = useQueryClient();

  const n = useNotification();
  const patchUppdragMutation = useMutation({
    ...patchUppdrag(),
    onSuccess: () => {
      n.success("Tidsrapportering bekräftad");
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetOne] });
      qc.invalidateQueries({ queryKey: [UppdragQueryKey.GetList] });
    },
    onError: () => {
      n.error();
    },
  });

  const timeReportData = uppdrag.timeReportData;
  const formatHours = (reported?: string | null, estimated?: string | null) =>
    reported
      ? `${reported} h`
      : estimated
        ? `${estimated} h (uppskattat)`
        : "";

  return (
    <Item>
      <Label>{`Tidsrapportering`}</Label>
      <Value className="flex items-center">
        <div className="flex flex-col">
          <span>
            {`Arbetad tid: ${formatHours(
              timeReportData?.reportedWorkingHours,
              timeReportData?.estimatedWorkingHours,
            )}`}
          </span>
          <span>
            {`Körtid: ${formatHours(
              timeReportData?.reportedDrivingHours,
              timeReportData?.estimatedDrivingHours,
            )}`}
          </span>
        </div>
        {isWaitingForConfirmation ? (
          <div className="ml-2 flex items-center space-x-2">
            <Dot />
            <PrimaryButton
              size="small"
              onClick={() =>
                patchUppdragMutation.mutate({
                  id: uppdrag.id,
                  payload: {
                    isTimeReportConfirmed: true,
                  },
                })
              }
            >
              Bekräfta
            </PrimaryButton>
          </div>
        ) : timeReportData?.isTimeReportConfirmed ? (
          <CheckCircleIcon className="ml-1 h-5 w-5 text-cyan-500" />
        ) : null}
      </Value>
    </Item>
  );
};
